import React, { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { IconChevronDown } from "@tabler/icons-react"
import type { ILocation } from "@dc-ireland/shared"
import apiClient from "../../lib/api"
import { CHAPTER_REGISTRY, chapterPath, getActiveChapterSlug } from "../../lib/chapters"

type ChapterNavItem = {
  slug: string
  name: string
  href: string
  status: "live" | "coming-soon"
}

const buildChapterNavItems = (locations: ILocation[]): ChapterNavItem[] => {
  const locationMap = new Map(locations.map((l) => [l.slug, l]))

  return CHAPTER_REGISTRY.map((chapter) => {
    const location = locationMap.get(chapter.slug)
    return {
      slug: chapter.slug,
      name: location?.name.replace(" HQ", "") ?? chapter.name,
      href: location?.pagePath ?? chapterPath(chapter.slug),
      status: chapter.status,
    }
  })
}

type Props = {
  active: boolean
  mobile?: boolean
  onNavigate?: () => void
}

export const ChaptersDropdown: React.FC<Props> = ({ active, mobile = false, onNavigate }) => {
  const [open, setOpen] = useState(false)
  const [chapters, setChapters] = useState<ChapterNavItem[]>(
    CHAPTER_REGISTRY.map((c) => ({
      slug: c.slug,
      name: c.name,
      href: chapterPath(c.slug),
      status: c.status,
    }))
  )
  const containerRef = useRef<HTMLLIElement>(null)
  const prefersReducedMotion = useReducedMotion()
  const location = useLocation()
  const activeChapterSlug = getActiveChapterSlug(location.pathname)

  useEffect(() => {
    apiClient.getLocations().then((locations) => {
      setChapters(buildChapterNavItems(locations))
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  const linkBase = mobile
    ? "font-dm-sans text-base tracking-wide transition-all"
    : "font-dm-sans text-sm tracking-wide transition-all"

  const triggerClass = [
    linkBase,
    "inline-flex items-center gap-1 font-medium",
    active
      ? "font-semibold text-white opacity-100 underline decoration-[#9e7e43] decoration-2 underline-offset-4"
      : "text-white opacity-90 hover:opacity-100",
  ].join(" ")

  if (mobile) {
    return (
      <li ref={containerRef as React.RefObject<HTMLLIElement>}>
        <button
          type="button"
          className={`${triggerClass} w-full justify-between py-1`}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          Chapters
          <IconChevronDown
            size={16}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        <AnimatePresence>
          {open && (
            <motion.ul
              initial={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-2 flex flex-col gap-1 overflow-hidden border-l border-[#9e7e43]/30 pl-4"
            >
              {chapters.map((chapter) => {
                const isActive = activeChapterSlug === chapter.slug

                return (
                <li key={chapter.slug}>
                  <Link
                    to={chapter.href}
                    className={[
                      "flex items-center gap-2 py-1.5 font-dm-sans text-sm transition",
                      isActive
                        ? "font-semibold text-[#9e7e43] underline decoration-2 underline-offset-4"
                        : "text-white/80 hover:text-white",
                    ].join(" ")}
                    onClick={onNavigate}
                  >
                    {chapter.name}
                    {chapter.status === "coming-soon" && (
                      <span className="rounded-full bg-[#9e7e43]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#9e7e43]">
                        Soon
                      </span>
                    )}
                  </Link>
                </li>
              )})}
            </motion.ul>
          )}
        </AnimatePresence>
      </li>
    )
  }

  return (
    <li ref={containerRef} className="relative">
      <button
        type="button"
        className={triggerClass}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        Chapters
        <IconChevronDown
          size={14}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-full z-30 mt-3 min-w-[200px] overflow-hidden rounded-xl border border-[#b79591]/30 bg-[rgba(13,13,13,0.97)] py-2 shadow-xl backdrop-blur-xl"
          >
            {chapters.map((chapter) => {
              const isActive = activeChapterSlug === chapter.slug

              return (
              <Link
                key={chapter.slug}
                to={chapter.href}
                className={[
                  "flex items-center justify-between gap-3 px-4 py-2.5 font-dm-sans text-sm transition",
                  isActive
                    ? "bg-[#9e7e43]/15 font-semibold text-[#9e7e43]"
                    : "text-white/85 hover:bg-white/5 hover:text-white",
                ].join(" ")}
                onClick={() => setOpen(false)}
              >
                {chapter.name}
                {chapter.status === "coming-soon" && (
                  <span className="shrink-0 rounded-full bg-[#9e7e43]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#9e7e43]">
                    Soon
                  </span>
                )}
              </Link>
            )})}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  )
}

export { isChaptersNavActive } from "../../lib/chapters"
